export const customerSecretTargets = [
  {
    id: 'senpicSenbos',
    label: 'Senbos Api Secret',
    pathTail: 'senpic/senbos',
    configPath: ['Senpic', 'Senbos', 'ApiUsernamePasswordSecret'] as const,
    secretKind: 'credentials' as const,
  },
  {
    id: 'senpicStagingSenbos',
    label: 'Staging Senbos Api Secret',
    pathTail: 'senpic/stagingSenbos',
    configPath: [
      'Senpic',
      'StagingSenbos',
      'ApiUsernamePasswordSecret',
    ] as const,
    secretKind: 'credentials' as const,
  },
  {
    id: 'senshieldSenbos',
    label: 'Senshield Senbos Api Secret',
    pathTail: 'senshield/senbos',
    configPath: ['Senshield', 'Senbos', 'ApiUsernamePasswordSecret'] as const,
    secretKind: 'credentials' as const,
  },
  {
    id: 'senforceSenbos',
    label: 'Senforce Senbos Api Secret',
    pathTail: 'senforce/senbos',
    configPath: ['Senforce', 'Senbos', 'ApiUsernamePasswordSecret'] as const,
    secretKind: 'credentials' as const,
  },
  {
    id: 'alertReviewerSenbos',
    label: 'Alert Reviewer Senbos Api Secret',
    pathTail: 'alertReviewer/senbos',
    configPath: [
      'AlertReviewer',
      'Senbos',
      'ApiUsernamePasswordSecret',
    ] as const,
    secretKind: 'credentials' as const,
  },
  {
    id: 'alertReviewerGptApiKey',
    label: 'GPT API Key',
    pathTail: 'alertReviewer/gpt/apiKey',
    configPath: ['AlertReviewer', 'Gpt', 'ApiKey'] as const,
    secretKind: 'apiKey' as const,
  },
  {
    id: 'alertReviewerClaudeApiKey',
    label: 'Claude API Key',
    pathTail: 'alertReviewer/claude/apiKey',
    configPath: ['AlertReviewer', 'Claude', 'ApiKey'] as const,
    secretKind: 'apiKey' as const,
  },
  {
    id: 'alertReviewerGeminiApiKey',
    label: 'Gemini API Key',
    pathTail: 'alertReviewer/gemini/apiKey',
    configPath: ['AlertReviewer', 'Gemini', 'ApiKey'] as const,
    secretKind: 'apiKey' as const,
  },
] as const;

export type CustomerSecretTarget = (typeof customerSecretTargets)[number];
export type CustomerSecretTargetId = CustomerSecretTarget['id'];

type CustomerSecretDraftNone = {
  action: 'none';
};

type CustomerSecretDraftUpsert = {
  action: 'upsert';
  username: string;
  password: string;
};

type CustomerSecretDraftDelete = {
  action: 'delete';
};

export type CustomerSecretDraft =
  | CustomerSecretDraftNone
  | CustomerSecretDraftUpsert
  | CustomerSecretDraftDelete;

export type CustomerSecretDrafts = Record<
  CustomerSecretTargetId,
  CustomerSecretDraft
>;

export const createSecretUpsertDraft = (
  credentials: { username: string; password: string } | { apiKey: string },
): CustomerSecretDraftUpsert => ({
  action: 'upsert',
  username: 'apiKey' in credentials ? '' : credentials.username,
  password: 'apiKey' in credentials ? credentials.apiKey : credentials.password,
});

export const createDefaultCustomerSecretDrafts = (): CustomerSecretDrafts => ({
  senpicSenbos: { action: 'none' },
  senpicStagingSenbos: { action: 'none' },
  senshieldSenbos: { action: 'none' },
  senforceSenbos: { action: 'none' },
  alertReviewerSenbos: { action: 'none' },
  alertReviewerGptApiKey: { action: 'none' },
  alertReviewerClaudeApiKey: { action: 'none' },
  alertReviewerGeminiApiKey: { action: 'none' },
});

export const getCustomerSecretStatusText = (
  draft: CustomerSecretDraft,
): string => {
  if (draft.action === 'upsert') {
    return 'Secret will update when Save is clicked';
  }

  if (draft.action === 'delete') {
    return 'Secret will be deleted when Save is clicked';
  }

  return '';
};

export type CustomerSecretOperation =
  | {
      type: 'upsert';
      target: CustomerSecretTarget;
      secretJson: string;
    }
  | {
      type: 'delete';
      target: CustomerSecretTarget;
    };

export const buildCustomerSecretOperations = (
  drafts: CustomerSecretDrafts,
  options?: { includeDelete?: boolean },
): CustomerSecretOperation[] => {
  const includeDelete = options?.includeDelete ?? true;

  return customerSecretTargets.reduce<CustomerSecretOperation[]>(
    (t, target) => {
      const draft = drafts[target.id];

      if (draft.action === 'upsert') {
        const secretJson =
          target.secretKind === 'apiKey'
            ? JSON.stringify({
                apiKey: draft.password,
              })
            : JSON.stringify({
                username: draft.username,
                password: draft.password,
              });
        t.push({ type: 'upsert', target, secretJson });
        return t;
      }

      if (draft.action === 'delete' && includeDelete) {
        t.push({
          type: 'delete',
          target,
        });
      }

      return t;
    },
    [],
  );
};

const setValueAtPath = (
  value: Record<string, unknown>,
  path: readonly string[],
  nextValue: string,
): Record<string, unknown> => {
  if (path.length === 0) {
    return value;
  }

  const [key, ...rest] = path;
  if (rest.length === 0) {
    return {
      ...value,
      [key]: nextValue,
    };
  }

  const next = value[key];
  const nextObject =
    next && typeof next === 'object' && !Array.isArray(next)
      ? (next as Record<string, unknown>)
      : {};

  return {
    ...value,
    [key]: setValueAtPath(nextObject, rest, nextValue),
  };
};

export const applyCustomerSecretConfigValues = (
  config: unknown,
  values: Partial<Record<CustomerSecretTargetId, string>>,
): unknown => {
  if (!config || typeof config !== 'object' || Array.isArray(config)) {
    return config;
  }

  return customerSecretTargets.reduce<Record<string, unknown>>(
    (t, target) => {
      const nextValue = values[target.id];
      if (typeof nextValue !== 'string') {
        return t;
      }

      return setValueAtPath(t, target.configPath, nextValue);
    },
    { ...(config as Record<string, unknown>) },
  );
};

const clearSecretAtPath = (
  value: Record<string, unknown>,
  path: readonly string[],
): Record<string, unknown> => {
  if (path.length === 0) {
    return value;
  }

  const [key, ...rest] = path;
  if (!(key in value)) {
    return value;
  }

  if (rest.length === 0) {
    return {
      ...value,
      [key]: '',
    };
  }

  const next = value[key];
  if (!next || typeof next !== 'object' || Array.isArray(next)) {
    return value;
  }

  return {
    ...value,
    [key]: clearSecretAtPath(next as Record<string, unknown>, rest),
  };
};

export const stripCustomerSecretValues = (config: unknown): unknown => {
  if (!config || typeof config !== 'object' || Array.isArray(config)) {
    return config;
  }

  return customerSecretTargets.reduce<Record<string, unknown>>(
    (t, target) => {
      return clearSecretAtPath(t, target.configPath);
    },
    { ...(config as Record<string, unknown>) },
  );
};
